"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { CitationChaserStrings } from "@/components/tools/citation-chaser";
import { ChaseTable } from "@/components/tools/research-discovery/chase-table";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import type { ChaseResult } from "@/lib/tool-kit/research-discovery/chase-run";
import {
  type ChaseHit,
  doiList,
  FORWARD_CAP,
  type ForwardOrder,
  hitsTsv,
  MAX_SEEDS,
  overlapCount,
  unionHits,
} from "@/lib/tool-kit/research-discovery/citation-chaser";
import { recordsToRis } from "@/lib/tool-kit/research-discovery/records";

export interface Finished {
  result: ChaseResult;
  date: string;
  order: ForwardOrder;
}

const PAGE = 100;
const RIS_MIME = "application/x-research-info-systems;charset=utf-8";

type View = "backward" | "forward";
type Fmt = (value: number) => string;

function seedState(
  state: string,
  strings: CitationChaserStrings,
  fmt: Fmt
): string {
  const text = (strings.seedStates as Record<string, string>)[state] ?? "";
  return t(text, { max: fmt(MAX_SEEDS) });
}

function recordText(
  finished: Finished,
  strings: CitationChaserStrings,
  fmt: Fmt,
  seedCount: number
): string {
  const { result, date, order } = finished;
  const base = t(strings.recordText, {
    seeds: fmt(seedCount),
    date,
    backward: fmt(result.backward.length),
    forward: fmt(result.forward.length),
  });
  if (!result.forwardTruncated) {
    return base;
  }
  const extra =
    order === "newest" ? strings.recordNewest : strings.recordMostCited;
  return `${base}${t(extra, { cap: fmt(FORWARD_CAP) })}`;
}

function viewNote(
  view: View,
  result: ChaseResult,
  strings: CitationChaserStrings,
  fmt: Fmt
): string {
  if (view === "forward") {
    const note = t(strings.forwardNote, { total: fmt(result.forwardTotal) });
    return result.forwardTruncated
      ? `${note} ${t(strings.forwardCut, { cap: fmt(FORWARD_CAP) })}`
      : note;
  }
  const note = t(strings.backwardNote, { total: fmt(result.backwardTotal) });
  return result.backwardCapped
    ? `${note} ${t(strings.backwardCut, { cap: fmt(result.backward.length) })}`
    : note;
}

function SeedList({
  result,
  strings,
  fmt,
}: {
  result: ChaseResult;
  strings: CitationChaserStrings;
  fmt: Fmt;
}) {
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.seedsHeading}</h3>
      <ul className="space-y-1 text-sm">
        {result.seeds.map((seed) => (
          <li key={seed.line.input}>
            <span className="font-medium text-foreground">
              {seed.work?.title || seed.line.doi || seed.line.input}
            </span>{" "}
            <span className="text-muted-foreground">
              {seed.work
                ? t(strings.seedDetail, {
                    year:
                      seed.work.year === null ? "–" : String(seed.work.year),
                    refs: fmt(seed.work.referenced.length),
                    citedBy: fmt(seed.work.citedBy),
                  })
                : seedState(
                    seed.status === "skipped" ? seed.line.state : seed.status,
                    strings,
                    fmt
                  )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function downloadList(list: readonly ChaseHit[], name: string) {
  downloadText(
    `${name}.ris`,
    recordsToRis(list.map((hit) => hit.record)),
    RIS_MIME
  );
}

export function ChaseResults({
  finished,
  strings,
}: {
  finished: Finished;
  strings: CitationChaserStrings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [view, setView] = useState<View>("backward");
  const [shown, setShown] = useState(PAGE);
  const { result } = finished;
  const hits = result[view];
  const seedCount = result.seeds.filter((s) => s.status === "found").length;
  const union = unionHits(result.backward, result.forward);
  const record = recordText(finished, strings, fmt, seedCount);
  const labels = {
    ...strings.columns,
    etAl: strings.etAl,
    untitled: strings.untitled,
    noDoi: strings.noDoi,
    caption: strings.tableCaption,
  };

  if (seedCount === 0) {
    return (
      <div className="space-y-4">
        <ErrorNotice>{strings.noneFound}</ErrorNotice>
        <SeedList fmt={fmt} result={result} strings={strings} />
      </div>
    );
  }

  return (
    <div className={`${RESULT_CARD} space-y-5`}>
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      {result.failedCalls > 0 ? (
        <ErrorNotice>
          {tPlural(strings.incomplete, locale, result.failedCalls, {
            count: fmt(result.failedCalls),
          })}
        </ErrorNotice>
      ) : null}
      <StatGrid>
        <StatCard
          label={strings.statBackward}
          value={fmt(result.backward.length)}
        />
        <StatCard
          label={strings.statForward}
          value={fmt(result.forward.length)}
        />
        <StatCard
          label={strings.statBoth}
          value={fmt(overlapCount(result.backward, result.forward))}
        />
      </StatGrid>
      <SeedList fmt={fmt} result={result} strings={strings} />
      <PillGroup
        label={strings.viewLabel}
        name="cch-view"
        onChange={(next) => {
          setView(next);
          setShown(PAGE);
        }}
        options={[
          {
            value: "backward",
            label: t(strings.backwardTab, {
              count: fmt(result.backward.length),
            }),
          },
          {
            value: "forward",
            label: t(strings.forwardTab, { count: fmt(result.forward.length) }),
          },
        ]}
        value={view}
      />
      <p className={HINT}>{viewNote(view, result, strings, fmt)}</p>
      {hits.length === 0 ? (
        <Notice>{strings.emptyList}</Notice>
      ) : (
        <>
          <ChaseTable
            fmt={fmt}
            hits={hits.slice(0, shown)}
            labels={labels}
            showSeeds={seedCount > 1}
          />
          {hits.length > shown ? (
            <button
              className={BUTTON_SECONDARY}
              onClick={() => setShown((current) => current + PAGE)}
              type="button"
            >
              {t(strings.showMore, {
                count: fmt(Math.min(PAGE, hits.length - shown)),
              })}
            </button>
          ) : null}
          <div className="flex flex-wrap gap-3">
            <button
              className={BUTTON_PRIMARY}
              onClick={() =>
                downloadList(
                  hits,
                  view === "backward"
                    ? strings.fileBackward
                    : strings.fileForward
                )
              }
              type="button"
            >
              {strings.downloadRis}
            </button>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyDois}
              text={doiList(hits)}
            />
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyTable}
              text={hitsTsv(hits)}
            />
            <button
              className={BUTTON_SECONDARY}
              onClick={() => downloadList(union, strings.fileBoth)}
              type="button"
            >
              {strings.downloadBoth}
            </button>
          </div>
        </>
      )}
      <p className={HINT}>
        {t(strings.dedupeNote, { union: fmt(union.length) })}
      </p>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.recordHeading}</h3>
        <p className="rounded-lg border border-border bg-card p-3 text-foreground text-sm">
          {record}
        </p>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.recordCopy}
          text={record}
        />
      </div>
      <p className={HINT}>{strings.indexNote}</p>
    </div>
  );
}
